import { FC } from 'react';

import { IRoutingConfigPolicyCondition } from '@entities/routing/config-version';
import { Chip, Stack, Typography } from '@mui/material';

interface IProps {
  conditions: IRoutingConfigPolicyCondition[];
}

// The API emits these dimensions as an array or a bare string.
const toArray = (value?: string[] | string) => (Array.isArray(value) ? value : value ? [value] : []);

// Same "Any ..." fallback semantics as the traffic column of the editable
// policies widget (@widgets/settings/policies renderTraffic).
const PolicyConditions: FC<IProps> = ({ conditions }) => {
  if (!conditions?.length) return <Typography variant='body2'>Any traffic</Typography>;

  return (
    <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
      {conditions.map((item, idx) => {
        const currencies = toArray(item.currency);
        const methods = toArray(item.method);
        const traffic = toArray(item.traffic);

        return (
          <Stack key={idx} spacing={0.75} sx={{ alignItems: 'flex-start', width: '100%' }}>
            <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
              {currencies.length ? (
                currencies.map(currency => (
                  <Chip key={currency} sx={{ fontWeight: 600 }} size='small' label={currency} />
                ))
              ) : (
                <Chip sx={{ fontWeight: 600 }} size='small' label='Any currency' />
              )}
              {methods.length ? (
                methods.map(method => (
                  <Chip key={method} size='small' variant='filled' label={method} />
                ))
              ) : (
                <Chip size='small' variant='filled' label='Any method' />
              )}
            </Stack>

            <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
              {traffic.length ? (
                traffic.map(trafficItem => (
                  <Chip key={trafficItem} size='small' variant='filled' label={trafficItem} />
                ))
              ) : (
                <Chip size='small' variant='filled' label='Any traffic' />
              )}
              <Typography variant='caption' color='text.secondary'>
                for
              </Typography>
              {item.merchants?.length ? (
                item.merchants.map(merchant => (
                  <Chip
                    key={merchant}
                    className='routing-merchant-chip'
                    size='small'
                    variant='filled'
                    label={merchant}
                  />
                ))
              ) : (
                <Chip
                  className='routing-merchant-chip'
                  size='small'
                  variant='filled'
                  label='Any merchant'
                />
              )}
            </Stack>

            {idx < conditions.length - 1 && <hr className='routing-config-condition-divider' />}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default PolicyConditions;
